import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { emailTemplateService } from '../../../services/emailTemplateService';

const TemplateLibrary = ({
  templates,
  loading,
  onEdit,
  onDelete,
  onDuplicate,
  onPreview,
  onSchedule
}) => {
  const [addingPreset, setAddingPreset] = React.useState(null);

  const getSegmentIcon = (segment) => {
    const icons = {
      high_value: 'TrendingUp',
      repeat: 'Repeat',
      at_risk: 'AlertTriangle',
      all_clients: 'Globe',
      feedback_low_rating: 'ThumbsDown',
      feedback_critical_issue: 'AlertOctagon',
      feedback_positive: 'ThumbsUp'
    };
    return icons?.[segment] || 'Mail';
  };

  const getSegmentLabel = (segment) => {
    const labels = {
      high_value: 'High-Value Clients',
      repeat: 'Repeat Clients',
      at_risk: 'At-Risk Clients',
      all_clients: 'All Clients',
      feedback_low_rating: 'Low Rating Follow-up',
      feedback_critical_issue: 'Critical Issue Alert',
      feedback_positive: 'Positive Feedback'
    };
    return labels?.[segment] || segment;
  };

  const getSegmentColor = (segment) => {
    const colors = {
      high_value: 'bg-green-100 text-green-800',
      repeat: 'bg-blue-100 text-blue-800',
      at_risk: 'bg-orange-100 text-orange-800',
      all_clients: 'bg-purple-100 text-purple-800',
      feedback_low_rating: 'bg-red-100 text-red-800',
      feedback_critical_issue: 'bg-orange-100 text-orange-800',
      feedback_positive: 'bg-green-100 text-green-800'
    };
    return colors?.[segment] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      draft: 'bg-yellow-100 text-yellow-800',
      archived: 'bg-gray-100 text-gray-800'
    };
    return colors?.[status] || colors?.draft;
  };

  const handleAddPreset = async (presetId) => {
    try {
      setAddingPreset(presetId);
      await emailTemplateService?.createFeedbackTemplate(presetId);
      window.location?.reload();
    } catch (error) {
      console.error('Error adding preset template:', error);
      alert('Failed to add preset template');
    } finally {
      setAddingPreset(null);
    }
  };

  const feedbackPresets = emailTemplateService?.getFeedbackTemplatePresets();
  const existingPresetIds = templates
    ?.filter(t => t?.metadata?.isPreset)
    ?.map(t => `preset-${t?.segment?.replace('feedback_', '')}`);
  const availablePresets = feedbackPresets?.filter(
    preset => !existingPresetIds?.includes(preset?.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {availablePresets?.length > 0 && (
        <div className="mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-500 rounded-lg">
              <Icon name="Sparkles" size={24} className="text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                Feedback-Specific Email Templates
              </h3>
              <p className="text-sm text-muted-foreground">
                Quick-add preset templates with Spanish support and automation triggers
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {availablePresets?.map((preset) => (
              <div
                key={preset?.id}
                className="bg-white border border-border rounded-lg p-4 hover:shadow-md transition-smooth"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className={`p-2 rounded-lg ${getSegmentColor(preset?.segment)}`}>
                    <Icon name={getSegmentIcon(preset?.segment)} size={20} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-sm text-foreground mb-1">
                      {preset?.templateName}
                    </h4>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {preset?.description}
                    </p>
                  </div>
                </div>

                <div className="mb-3 space-y-1">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="Zap" size={12} className="text-blue-500" />
                    <span className="font-medium">Trigger:</span>
                    <span className="text-xs">{preset?.triggerCondition}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="Code" size={12} className="text-green-500" />
                    <span className="font-medium">Variables:</span>
                    <span className="text-xs">{preset?.availableVariables?.length} available</span>
                  </div>
                </div>

                <button
                  onClick={() => handleAddPreset(preset?.id)}
                  disabled={addingPreset === preset?.id}
                  className="w-full px-3 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth press-effect focus-ring text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addingPreset === preset?.id ? (
                    <>
                      <Icon name="Loader2" size={14} className="inline animate-spin mr-1" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Icon name="Plus" size={14} className="inline mr-1" />
                      Add Template
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-start gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <Icon name="Info" size={14} className="mt-0.5 flex-shrink-0" />
            <p>
              These templates include Spanish language support, variable insertion (client_name, folio_ot, satisfaction_rating, service_comments), 
              and are ready for integration with automation rules for triggered sending based on feedback conditions.
            </p>
          </div>
        </div>
      )}

      {templates?.length === 0 ? (
        <div className="text-center py-16">
          <Icon name="Mail" size={64} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-foreground mb-2">
            No Templates Found
          </h3>
          <p className="text-muted-foreground mb-6">
            Create your first email template or add a preset feedback template above
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates?.map((template) => (
            <div
              key={template?.id}
              className="bg-card border border-border rounded-lg p-6 hover:shadow-elevation-2 transition-smooth"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Icon name={getSegmentIcon(template?.segment)} size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{template?.templateName}</h3>
                    <p className="text-sm text-muted-foreground">
                      {getSegmentLabel(template?.segment)}
                    </p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(template?.status)}`}>
                  {template?.status}
                </span>
              </div>

              <div className="mb-4">
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {template?.subjectLine}
                </p>
              </div>

              <div className="flex items-center gap-4 mb-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Icon name="Send" size={14} />
                  <span>{template?.usageCount || 0} sent</span>
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="FileText" size={14} />
                  <span>v{template?.version || 1}</span>
                </div>
                {template?.lastUsedAt && (
                  <div className="flex items-center gap-1">
                    <Icon name="Clock" size={14} />
                    <span>{new Date(template?.lastUsedAt)?.toLocaleDateString('es-MX')}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => onEdit(template)}
                  className="flex-1 px-3 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-smooth press-effect focus-ring text-sm"
                >
                  <Icon name="Edit" size={16} className="inline mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => onPreview(template)}
                  className="flex-1 px-3 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-smooth press-effect focus-ring text-sm"
                >
                  <Icon name="Eye" size={16} className="inline mr-1" />
                  Preview
                </button>
              </div>

              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => onSchedule(template)}
                  className="flex-1 px-3 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-smooth press-effect focus-ring text-sm"
                >
                  <Icon name="Calendar" size={16} className="inline mr-1" />
                  Schedule
                </button>
                <button
                  onClick={() => onDuplicate(template?.id)}
                  className="px-3 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-smooth press-effect focus-ring text-sm"
                >
                  <Icon name="Copy" size={16} />
                </button>
                <button
                  onClick={() => onDelete(template?.id)}
                  className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-smooth press-effect focus-ring text-sm"
                >
                  <Icon name="Trash2" size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TemplateLibrary;